import React, { useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { Download, Chrome, Globe, Command, Box, Terminal, AlertTriangle } from 'lucide-react';

const BrowserCard = ({ name, description, version, icon: Icon, primary = false }) => (
    <div className={`
        relative p-6 border transition-all cursor-pointer group flex flex-col justify-between h-48
        ${primary
            ? 'bg-[var(--bg-main)] border-[var(--fg-primary)] hover:bg-[var(--fg-primary)] hover:text-[var(--bg-main)]'
            : 'bg-[var(--bg-panel)] border-[var(--border-main)] hover:border-[var(--fg-secondary)]'
        }
    `}>
        <div className="flex justify-between items-start">
            <Icon size={32} strokeWidth={1} className={primary ? "text-inherit" : "text-[var(--fg-secondary)]"} />
            <span className={`text-[10px] font-mono uppercase tracking-wider ${primary ? "text-inherit" : "text-[var(--fg-tertiary)]"}`}>
                v{version}
            </span>
        </div>

        <div>
            <h3 className={`font-serif text-2xl mb-2 ${primary ? "text-inherit" : "text-[var(--fg-primary)]"}`}>
                {name}
            </h3>
            <p className={`font-mono text-xs leading-relaxed ${primary ? "text-inherit opacity-80" : "text-[var(--fg-secondary)]"}`}>
                {description}
            </p>
        </div>

        <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
            <Download size={20} />
        </div>
    </div>
);

const InstallationPage = () => {
    useEffect(() => {
        document.title = "AltFinder / Deploy Extension";
    }, []);

    return (
        <AppLayout>
            <div className="max-w-5xl mx-auto p-12 h-full overflow-y-auto">
                <div className="mb-16 border-b border-[var(--border-main)] pb-8">
                    <h1 className="font-serif text-4xl text-[var(--fg-primary)] mb-4">Extension Deployment</h1>
                    <p className="font-mono text-sm text-[var(--fg-tertiary)] uppercase tracking-widest max-w-2xl leading-relaxed">
                        Inject the AltFinder Index directly into your browsing workflow.
                        Our client-side extension analyzes current domains and suggests ethical alternatives in real-time.
                    </p>
                </div>

                {/* Primary Browsers */}
                <div className="mb-20">
                    <div className="flex items-center gap-4 mb-8">
                        <span className="h-px bg-[var(--border-main)] flex-1"></span>
                        <span className="font-mono text-xs text-[var(--fg-tertiary)] uppercase tracking-widest">Supported Environments</span>
                        <span className="h-px bg-[var(--border-main)] flex-1"></span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <BrowserCard
                            name="Google Chrome"
                            description="Available on Chrome Web Store. Manifest V3 Compliant."
                            version="2.0.4"
                            icon={Chrome}
                            primary={true}
                        />
                        <BrowserCard
                            name="Firefox"
                            description="Mozilla Add-ons (AMO). Recommended for privacy."
                            version="2.0.4"
                            icon={Globe}
                        />
                        <BrowserCard
                            name="Microsoft Edge"
                            description="Edge Add-ons. Enterprise ready."
                            version="2.0.4"
                            icon={Box}
                        />
                        <BrowserCard
                            name="Safari"
                            description="macOS 12+ / iOS 15+. Universal Binary."
                            version="1.8.2"
                            icon={Command}
                        />
                        <BrowserCard
                            name="Brave / Chromium"
                            description="Compatible via Chrome Store. Shield-friendly."
                            version="2.0.4"
                            icon={Chrome}
                        />
                    </div>
                </div>

                {/* Manual Install / Developer Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div className="bg-[#0f0f0f] border border-[var(--border-main)] p-8">
                        <div className="flex items-center gap-3 mb-6 text-[var(--signal-green)]">
                            <Terminal size={20} />
                            <h3 className="font-mono text-sm uppercase tracking-widest">Manual Load (Developer)</h3>
                        </div>
                        <p className="font-mono text-xs text-[var(--fg-secondary)] mb-6 leading-relaxed">
                            For security auditing or contribution, you can load the unpacked extension directly from source.
                        </p>
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 font-mono text-xs text-[var(--fg-primary)]">
                                <span className="text-[var(--fg-tertiary)]">01.</span>
                                <span>git clone https://github.com/altfinder/ext.git</span>
                            </div>
                            <div className="flex items-center gap-4 font-mono text-xs text-[var(--fg-primary)]">
                                <span className="text-[var(--fg-tertiary)]">02.</span>
                                <span>npm run build:ext</span>
                            </div>
                            <div className="flex items-center gap-4 font-mono text-xs text-[var(--fg-primary)]">
                                <span className="text-[var(--fg-tertiary)]">03.</span>
                                <span>Load /dist folder in browser Developer Mode</span>
                            </div>
                        </div>
                    </div>

                    <div className="border border-[var(--border-main)] p-8 bg-[var(--bg-panel)] flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-4 text-[var(--fg-primary)]">
                            <AlertTriangle size={20} />
                            <h3 className="font-serif text-xl">Privacy Notice</h3>
                        </div>
                        <p className="font-mono text-xs text-[var(--fg-secondary)] leading-relaxed mb-6">
                            The extension operates entirely locally. It compares the current URL hash against a local Bloom filter of known domains.
                            Your browsing history never leaves your device.
                        </p>
                        <button className="self-start text-[10px] font-mono uppercase bg-[var(--bg-main)] border border-[var(--border-main)] px-4 py-2 hover:bg-[var(--fg-primary)] hover:text-[var(--bg-main)] transition-colors">
                            View Source Code
                        </button>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default InstallationPage;
